/**
 * OrbieeFlow — run one agent turn and apply everything it asked for.
 *
 * A turn may call several tools ("add this to my bag and show me something
 * similar" → `add_to_cart` + `search_products`). `orbieeChatTurn` merges those
 * tool results into ONE response whose `intent` array lists every recognised
 * tool in the order the agent ran them.
 *
 * This module is the executor for that array. It is deliberately split in two:
 *
 *   • Side effects (cart toast, wishlist sync, try-on overlay, price-match
 *     alert, recommendation payload) — ALL of them run, every turn, for every
 *     intent present. They compose.
 *
 *   • Navigation — at most ONE runs, because the browser can only be on one
 *     route. The winner is chosen by {@link NAV_PRIORITY}, with the later
 *     intent winning a tie, so the agent's last navigational tool decides the
 *     landing page.
 *
 * Every navigation performed here is agent-driven, so it is wrapped in
 * `markAgentNavigation()` and the whole effect phase runs inside
 * `beginAgentAction()` / `endAgentAction()` — that is what tags the resulting
 * `view_changed` / `cart_changed` / `wishlist_changed` frames as agent echoes
 * instead of manual clicks.
 */

import {
  runOrbieeChatTurn,
  getOrbieeDeviceId,
  formatConversationForDisplay,
  isEmptyTranscriptError,
} from "../../services/websocket";
import { showAlert } from "../../utils/alertPopupManager";
import { showToast } from "../../utils/toastManager";
import {
  markAgentNavigation,
  beginAgentAction,
  endAgentAction,
  getScreenContext,
} from "../../services/uiAgentSync";
import {
  recordPriceMatch,
  productIdFromLocation,
  parsePriceMatchFromText,
  isPriceMatchSuccess,
} from "../../utils/priceMatch";
import { extractAgentBillingFromResponse } from "../../utils/agentBillingPayload";
import {
  showAgentCartToast,
  agentCartAddedProducts,
} from "../../utils/cartToastFromAgentResponse";
import { flyToBag, productImageOf } from "../../utils/flyToBag";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** sessionStorage keys this flow reads or writes. */
const SS = Object.freeze({
  CHAT_IS_OPEN: "orbiee_chat_is_open",
  CHAT_MANUALLY_CLOSED: "orbiee_chat_manually_closed",
  CHAT_SHOULD_BE_OPEN: "orbiee_chat_should_be_open",
  SIZE_CONFIRMATION: "orbiee_size_confirmation",
  USER_ANSWER: "user_answer",
  RECOMMENDATION_PRODUCTS: "orbiee_recommendation_products",
  VTO_OPEN: "orbiee_virtual_try_on_open",
  VTO_PRODUCT_ID: "orbiee_virtual_try_on_product_id",
  VTO_AUTOCLICK_PROFILE: "orbiee_virtual_try_on_autoclick_profile",
  VTO_CLOSE_REASON: "orbiee_virtual_try_on_close_reason",
});

/**
 * Which intent wins the single navigation slot when a turn produced several.
 *
 * The default rule is "the LAST tool the agent ran decides" — the same rule
 * the reference storefront gets for free by executing tool results as they
 * arrive. That is why almost everything shares one priority: ties are broken
 * by position in the intent array, which is tool order.
 *
 * Only two things override it:
 *   • Checkout is terminal. Nothing the agent tacked on afterwards should
 *     strand the shopper away from the confirmation they just triggered.
 *   • `add_to_wishlist` sits at the bottom. On a wishlist-only turn it still
 *     lands on /wishlist (existing behaviour), but it never hijacks a turn
 *     that also searched or opened a product.
 */
const NAV_PRIORITY = Object.freeze({
  confirm_purchase: 30,
  billing_confirmation: 20,
  order_history: 10,
  view_cart: 10,
  view_wishlist: 10,
  detail_page: 10,
  product_list: 10,
  add_to_wishlist: 0,
});

/** Intents treated as "the agent put a browsable list on screen". */
const BROWSABLE_LIST_INTENTS = Object.freeze([
  "self",
  "uncertain",
  "others",
  "search",
  "product_list",
]);

const FALLBACK_REPLY = "Excuse me! Can you come again please?";

// ─────────────────────────────────────────────────────────────────────────────
// Small helpers
// ─────────────────────────────────────────────────────────────────────────────

const isPlainObject = (value) =>
  value != null && typeof value === "object" && !Array.isArray(value);

/** `action` is only ever a plain object; anything else is treated as absent. */
const asPlainObject = (value) => (isPlainObject(value) ? value : {});

/** Normalise `intent` (string | string[] | null) to a string array. */
function toIntentList(raw) {
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  if (raw != null && raw !== "") return [String(raw)];
  return [];
}

/** sessionStorage throws in Safari private mode — never let that kill a turn. */
function ssGet(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function ssSet(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* storage unavailable — non-fatal */
  }
}

function ssRemove(key) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* storage unavailable — non-fatal */
  }
}

/**
 * Keep the chat panel open across an agent-driven navigation, unless the
 * shopper closed it by hand.
 */
function markChatShouldStayOpen() {
  const chatIsOpen = ssGet(SS.CHAT_IS_OPEN) === "true";
  const wasManuallyClosed = ssGet(SS.CHAT_MANUALLY_CLOSED) === "true";
  if (chatIsOpen && !wasManuallyClosed) {
    ssSet(SS.CHAT_SHOULD_BE_OPEN, "true");
  }
}

/**
 * Clear the one-shot context attached to a single utterance.
 *
 * Runs on the failure path too: a turn that throws with
 * `orbiee_size_confirmation` still set would send `ctx_intent_override=
 * add_to_cart` on the next, unrelated message.
 */
function clearOneShotTurnContext() {
  ssSet(SS.USER_ANSWER, "");
  ssSet(SS.SIZE_CONFIRMATION, "false");
}

// ─────────────────────────────────────────────────────────────────────────────
// Turn request
// ─────────────────────────────────────────────────────────────────────────────

/** Per-customer conversation id, so a reload resumes the same thread. */
const conversationIdKeyFor = (customerId) =>
  `orbiee_conversation_id_${customerId}`;

/** `ctx_*` query params carried on the WebSocket frame for this turn. */
function buildTurnQueryParams(customerId) {
  const params = {};

  const conversationId = ssGet(conversationIdKeyFor(customerId));
  if (conversationId) params.ctx_conversation_id = String(conversationId);

  if (ssGet(SS.SIZE_CONFIRMATION) === "true") {
    params.ctx_intent_override = "add_to_cart";
  }

  const userAnswer = ssGet(SS.USER_ANSWER);
  if (userAnswer) params.ctx_user_answer = String(userAnswer);

  return params;
}

/** Extract `{ audioBlob, text }` from the caller's message payload. */
function readTurnPayload(messages) {
  const msgObj = Array.isArray(messages) ? messages[0] : messages;
  const raw = isPlainObject(msgObj) ? msgObj : {};

  const audioBlob =
    raw.audioBlob instanceof Blob || raw.audioBlob instanceof File
      ? raw.audioBlob
      : null;

  // Text path: a transcription string, chat content, or a legacy STT result.
  const text = audioBlob ? null : (raw.transcription ?? raw.content ?? null);

  return { audioBlob, text, hasTranscription: Boolean(raw.transcription) };
}

/** Wire mode: explicit caller choice wins, otherwise derive from the payload. */
function resolveTurnMode(flowOptions, audioBlob) {
  const requested = flowOptions.mode;
  if (requested === "text" || requested === "voice" || requested === "avatar") {
    return requested;
  }
  return audioBlob ? "voice" : "text";
}

// ─────────────────────────────────────────────────────────────────────────────
// Side effects — these all run, and they compose
// ─────────────────────────────────────────────────────────────────────────────

const CART_MUTATION_INTENTS = Object.freeze([
  "add_to_cart",
  "remove_from_cart",
  "clear_cart",
  "update_cart_quantity",
]);

const CART_MUTATION_TYPES = Object.freeze([
  "ADD_TO_CART",
  "REMOVE_FROM_CART",
  "CLEAR_CART",
  "UPDATE_CART_QUANTITY",
]);

/**
 * Cart mutations are silent: a toast, never a navigation. A combined turn
 * therefore still has its navigation slot free for a later tool.
 *
 * @returns {boolean} true when the agent is waiting on a size answer, which
 *   suspends every other effect — the turn is a question, not an outcome.
 */
function applyCartEffect(intents, action, lastResponse) {
  const mutation = asPlainObject(lastResponse.cart_mutation);
  const mutationType = mutation.type ? String(mutation.type) : "";

  const isCartTurn =
    intents.some((i) => CART_MUTATION_INTENTS.includes(i)) ||
    CART_MUTATION_TYPES.includes(mutationType);
  if (!isCartTurn) return false;

  const isAdd = intents.includes("add_to_cart") || mutationType === "ADD_TO_CART";
  if (isAdd) {
    // Adding to the bag ends a try-on popup; the reason lets the inline
    // mirror on the product page stay open (see the AvatarLogoCode poll).
    ssSet(SS.VTO_OPEN, "false");
    ssSet(SS.VTO_CLOSE_REASON, "add_to_cart");

    const pendingSize =
      action.status === "PENDING_SIZE_CONFIRMATION" ||
      mutation.status === "PENDING_SIZE_CONFIRMATION";
    if (pendingSize) {
      ssSet(SS.SIZE_CONFIRMATION, "true");
      return true;
    }
  }

  // Self-guarding: no-ops for a failed or pending mutation.
  showAgentCartToast(lastResponse);

  // Same guard as the toast: only a successful add flies into the bag, and it
  // leaves from the assistant, since that is who put it there.
  const added = agentCartAddedProducts(lastResponse);
  if (added !== null) {
    const first = added[0] || null;
    flyToBag("cart", {
      from: "agent",
      image: productImageOf(first),
      key: String(first?.id ?? first?.product_id ?? first?.article_id ?? "agent"),
    });
  }
  return false;
}

/** Open or close the virtual try-on overlay (an overlay, never a route). */
function applyVirtualTryOnEffect(intents, action) {
  if (intents.includes("close_virtual_try_on")) {
    // The overlay is driven by a sessionStorage poll, so mark the navigation
    // now for the `view_changed` the poll will emit.
    markAgentNavigation();
    ssSet(SS.VTO_OPEN, "false");
    ssRemove(SS.VTO_CLOSE_REASON);
    ssRemove(SS.VTO_PRODUCT_ID);
    ssRemove(SS.VTO_AUTOCLICK_PROFILE);
    return;
  }

  if (!intents.includes("virtual_try_on")) return;

  const productId = action.product_id != null ? String(action.product_id) : "";
  if (!productId) return;

  markAgentNavigation();
  ssRemove(SS.VTO_CLOSE_REASON);
  ssSet(SS.VTO_PRODUCT_ID, productId);
  ssSet(SS.VTO_OPEN, "true");
  ssSet(SS.VTO_AUTOCLICK_PROFILE, "true");
}

/**
 * Field names for a match vary across the tools that can report one, so read
 * the payload, its `raw` passthrough and the turn's own stash, first hit wins.
 */
function readPriceMatch(action, lastResponse) {
  const sources = [
    action,
    asPlainObject(action.raw),
    asPlainObject(lastResponse.price_match),
  ];
  const pick = (...keys) => {
    for (const src of sources) {
      for (const key of keys) {
        const v = src?.[key];
        if (v != null && v !== "") return v;
      }
    }
    return null;
  };
  return {
    // With no id in the payload, the product on screen is the one the shopper
    // and the agent were both talking about.
    productId:
      pick("product_id", "article_id", "id") ||
      getScreenContext()?.focused_product_id ||
      productIdFromLocation() ||
      null,
    matchedPrice: pick("matched_price", "new_price", "final_price", "found_price"),
    originalPrice: pick("original_price", "current_price", "old_price", "previous_price"),
    currency: pick("currency", "currency_code"),
    competitor: pick("competitor", "competitor_name", "retailer", "store"),
    expiresAt: pick("expires_at", "expiry", "valid_until", "expires"),
  };
}

/**
 * A match the shopper cannot see: the celebration lives on the product's own
 * page, so when the match lands anywhere else, say so with a toast instead.
 * On that page, the page is the announcement and a toast would double it.
 */
function announcePriceMatch(entry) {
  if (!entry) return;
  if (productIdFromLocation() === entry.productId) return;

  const saved =
    entry.originalPrice != null ? entry.originalPrice - entry.matchedPrice : 0;
  showToast({
    id: "price-match",
    title: "Price matched",
    message:
      `Now ${entry.currency} ${entry.matchedPrice.toFixed(2)}` +
      (saved > 0 ? ` — you save ${entry.currency} ${saved.toFixed(2)}` : ""),
  });
}

/**
 * Read a confirmed match out of the assistant's own words, for a deployment
 * that confirms in prose without the frontend ever seeing a price-match tool
 * result. Same safety net the wishlist helper keeps, and equally conservative.
 */
function priceMatchFromText(lastResponse) {
  const parsed = parsePriceMatchFromText(lastResponse.conversation_text);
  if (!parsed) return null;

  const productId =
    getScreenContext()?.focused_product_id || productIdFromLocation() || null;
  if (!productId) return null;

  return { productId, ...parsed, currency: null, competitor: null };
}

/**
 * Price-match outcome. The record is written for every surface — the product
 * page celebrates it wherever the turn came from — while the alert stays on
 * spoken surfaces, which have no chat transcript to read the outcome in.
 */
function applyPriceMatchEffect(intents, action, lastResponse, isSpokenTurn) {
  const structured =
    intents.includes("price_match") ||
    String(action.type || "") === "PRICE_MATCH" ||
    isPlainObject(lastResponse.price_match);

  if (structured) {
    // Judge the payload, not one spelling of it: the assembler normalises to
    // "SUCCESS", but a price-match action can also reach here straight from a
    // tool result still carrying its own wording ("PRICE_MATCHED").
    if (
      isPriceMatchSuccess(action) ||
      isPriceMatchSuccess(lastResponse.price_match)
    ) {
      announcePriceMatch(recordPriceMatch(readPriceMatch(action, lastResponse)));
    }
  } else {
    // No tool result to read: fall back to what the assistant said.
    const fromText = priceMatchFromText(lastResponse);
    if (fromText) announcePriceMatch(recordPriceMatch(fromText));
    return;
  }

  if (!isSpokenTurn) return;

  switch (action.status) {
    case "SUCCESS":
      showAlert({
        severity: "success",
        message: `Price matched! New price: ${action.currency} ${action.matched_price} (matched with ${action.competitor})`,
      });
      break;
    case "REJECTED":
      showAlert({
        severity: "error",
        message: action.reason || "Unable to match the price at this time.",
      });
      break;
    case "ERROR":
      showAlert({
        severity: "warning",
        message:
          action.message || "Could not process the price match request.",
      });
      break;
    case "FOLLOW_UP_REQUIRED": {
      const raw = asPlainObject(action.raw);
      showAlert({
        severity: "info",
        message:
          raw.message ||
          lastResponse.conversation_text ||
          "Please share the store and price you saw so we can verify.",
      });
      break;
    }
    default:
      break;
  }
}

/**
 * Did this turn put a browsable product list on screen?
 *
 * A combined turn keeps its cart mutation intent, so the presence of
 * `add_to_cart` must not disqualify a later `search_products`.
 */
function hasBrowsableProductList(intents, action, products) {
  if (!Array.isArray(products) || products.length === 0) return false;
  if (intents.length === 0) return true;
  if (intents.some((i) => BROWSABLE_LIST_INTENTS.includes(i))) return true;
  return action.type === "PRODUCT_LIST";
}

/**
 * Publish a fresh product list.
 *
 * Runs whenever the turn produced one, even when another intent wins the
 * navigation slot — /recommendations must hold the latest results whenever the
 * shopper gets there, and a page already mounted repaints on the event.
 */
function publishRecommendationProducts(products) {
  ssSet(SS.RECOMMENDATION_PRODUCTS, JSON.stringify(products));
  try {
    window.dispatchEvent(
      new CustomEvent("orbiee:productsUpdated", {
        detail: { products, timestamp: Date.now() },
      })
    );
  } catch (err) {
    console.warn("[OrbieeFlow] products update event failed", err);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Navigation — exactly one target per turn
// ─────────────────────────────────────────────────────────────────────────────

/** `detail_page` target, preferring `action.product` over the catalogue. */
function buildDetailPageTarget(action, products, lastResponse) {
  const actionProducts = Array.isArray(action.product) ? action.product : [];
  const product =
    actionProducts.length > 0
      ? actionProducts[0]
      : isPlainObject(action.product)
        ? action.product
        : products.length > 0
          ? products[0]
          : null;

  if (!isPlainObject(product) || !product.id) return null;

  return {
    path: `/item/${product.id}`,
    options: {
      state: {
        product,
        productDetail: action.product_detail ?? lastResponse.product_detail ?? null,
      },
    },
  };
}

function buildBillingTarget(action, lastResponse) {
  const { billing, lines, totals } = extractAgentBillingFromResponse(lastResponse);
  return {
    path: "/checkout",
    options: {
      state: {
        isAgentCheckout: true,
        fromChat: true,
        cartData: action.cart_data || lines || [],
        billingDetails: billing || action.billing_details || {},
        lines,
        totals,
      },
    },
  };
}

function buildPurchaseTarget(action, lastResponse) {
  const summary = asPlainObject(lastResponse.summary);
  const { lines, totals } = extractAgentBillingFromResponse(lastResponse);

  const purchasedItems = Array.isArray(action.purchased_items)
    ? action.purchased_items
    : Array.isArray(summary.items)
      ? summary.items
      : [];

  const articleIds = Array.isArray(action.article_ids)
    ? action.article_ids
    : Array.isArray(summary.article_ids)
      ? summary.article_ids
      : Array.isArray(summary.order_ids)
        ? summary.order_ids.map(String)
        : [];

  const totalAmount =
    totals?.total ?? action.total_amount ?? summary.total ?? lastResponse.total ?? 0;

  return {
    path: "/order-confirmation",
    options: {
      state: {
        isAgentFlow: true,
        fromChat: true,
        message:
          lastResponse.conversation_text ||
          "Your purchase has been completed by Orbiee Assistant.",
        totalAmount,
        purchasedItems,
        articleIds,
        summary,
        lines,
        totals,
        currency: totals?.currency || summary.currency || "USD",
      },
    },
  };
}

/**
 * Build the route for one intent, or null when that intent does not navigate.
 *
 * @param {string} intent
 * @param {{ action: object, products: object[], lastResponse: object, browsable: boolean }} ctx
 */
function buildNavigationTarget(intent, ctx) {
  const { action, products, lastResponse, browsable } = ctx;

  switch (intent) {
    case "detail_page":
      return buildDetailPageTarget(action, products, lastResponse);

    case "billing_confirmation":
      return buildBillingTarget(action, lastResponse);

    case "confirm_purchase":
      return buildPurchaseTarget(action, lastResponse);

    case "order_history":
      return { path: "/order-history", options: undefined };

    case "view_wishlist":
    case "add_to_wishlist":
      return { path: "/wishlist", options: undefined };

    case "view_cart": {
      // The agent sometimes reports `view_cart` while actually answering about
      // the wishlist; the prose is the tiebreaker.
      if (/wishlist/i.test(lastResponse.conversation_text || "")) return null;
      return { path: "/cart", options: undefined };
    }

    case "product_list":
      if (!browsable) return null;
      return {
        path: "/recommendations",
        options: { state: { products, timestamp: Date.now() }, replace: false },
      };

    default:
      return null;
  }
}

/**
 * Pick the single route this turn lands on: highest {@link NAV_PRIORITY}, and
 * on a tie the intent the agent produced last.
 */
function resolveNavigationTarget(intents, ctx) {
  let best = null;

  intents.forEach((intent, index) => {
    const priority = NAV_PRIORITY[intent];
    if (priority == null) return;
    if (best && priority < best.priority) return;
    if (best && priority === best.priority && index < best.index) return;

    const target = buildNavigationTarget(intent, ctx);
    if (!target) return;

    best = { ...target, priority, index, intent };
  });

  // A turn with no recognised intent but a product list still lands on the
  // results page — the agent searched without tagging the tool.
  if (!best && intents.length === 0 && ctx.browsable) {
    return buildNavigationTarget("product_list", ctx);
  }

  return best;
}

/**
 * Apply every effect this turn asked for, then navigate at most once.
 * Safe to call with any response shape.
 */
function applyTurnEffects(lastResponse, navigate, isSpokenTurn) {
  if (!isPlainObject(lastResponse)) return;

  const intents = toIntentList(lastResponse.intent);
  const action = asPlainObject(lastResponse.action);
  const products = Array.isArray(lastResponse.products)
    ? lastResponse.products
    : [];

  // 1 · Cart. A pending size question suspends the rest of the turn: the
  //     agent is asking, not acting, so nothing should move on screen.
  if (applyCartEffect(intents, action, lastResponse)) return;

  // 2 · Overlays and alerts — independent of whatever wins navigation.
  applyVirtualTryOnEffect(intents, action);
  applyPriceMatchEffect(intents, action, lastResponse, isSpokenTurn);

  // 3 · Publish results whether or not we land on them.
  const browsable = hasBrowsableProductList(intents, action, products);
  if (browsable) publishRecommendationProducts(products);

  // 4 · Exactly one navigation.
  const target = resolveNavigationTarget(intents, {
    action,
    products,
    lastResponse,
    browsable,
  });
  if (!target || typeof navigate !== "function") return;

  markChatShouldStayOpen();
  navigate(target.path, target.options);
}

// ─────────────────────────────────────────────────────────────────────────────
// Result text
// ─────────────────────────────────────────────────────────────────────────────

/**
 * What the chat bubble shows: the assistant body with its follow-up appended
 * when that is not already a restatement of the body.
 */
function displayTextFor(lastResponse) {
  if (!isPlainObject(lastResponse)) return FALLBACK_REPLY;
  const text = formatConversationForDisplay(
    lastResponse.conversation_text,
    lastResponse.followup
  );
  return text || FALLBACK_REPLY;
}

// ─────────────────────────────────────────────────────────────────────────────
// Entry point
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Run one agent turn and apply its outcome to the app.
 *
 * @param {object|object[]} messages    `{ content }` / `{ transcription }` / `{ audioBlob }`
 * @param {string|number}   customerId
 * @param {Function|null}   navigate    react-router `navigate`
 * @param {Function|null}   addToCart   reserved — cart state is applied by the
 *                                      caller's `onLastResponse` (CartContext)
 * @param {string}          languageCode
 * @param {Function|null}   onLastResponse  receives the raw merged response
 * @param {object|null}     options     `{ mode, languageCode, onLlmDisplayUpdate,
 *                                      onTranscript, onLlmResponse, onActivity }`
 * @returns {Promise<string>} text for the chat bubble
 */
export default async function OrbieeFlow(
  messages,
  customerId,
  navigate = null,
  addToCart = null,
  languageCode = "eng",
  onLastResponse = null,
  options = null
) {
  const flowOptions = isPlainObject(options) ? options : {};

  // Every navigation from here is agent-driven: tag it so its `view_changed`
  // effect event reports `source: "agent_navigation"`, not `manual_click`.
  const agentNavigate =
    typeof navigate === "function"
      ? (...args) => {
          markAgentNavigation();
          return navigate(...args);
        }
      : null;

  const { audioBlob, text, hasTranscription } = readTurnPayload(messages);
  const turnMode = resolveTurnMode(flowOptions, audioBlob);
  const isSpokenTurn = Boolean(audioBlob) || turnMode !== "text" || hasTranscription;

  const userId =
    customerId != null && customerId !== "" ? String(customerId) : "guest";

  let lastResponse;
  try {
    lastResponse = await runOrbieeChatTurn({
      userId,
      deviceId: await getOrbieeDeviceId(),
      message: text,
      audioBlob: audioBlob || undefined,
      languageCode: flowOptions.languageCode || languageCode,
      queryParams: buildTurnQueryParams(customerId),
      onLlmDisplayUpdate: flowOptions.onLlmDisplayUpdate,
      onTranscript: flowOptions.onTranscript,
      onLlmResponse: flowOptions.onLlmResponse,
      onActivity: flowOptions.onActivity,
      mode: turnMode,
    });
  } catch (error) {
    clearOneShotTurnContext();
    // Mic closed on silence — a soft cancel, not a crash. Rethrown so the
    // caller can restart listening instead of showing an error bubble.
    if (isEmptyTranscriptError(error)) {
      console.debug("[OrbieeFlow] empty transcript — soft cancel");
    } else {
      console.error("[OrbieeFlow] turn failed", error);
    }
    throw error;
  }
  clearOneShotTurnContext();

  if (lastResponse?.conversation_id) {
    ssSet(conversationIdKeyFor(customerId), String(lastResponse.conversation_id));
  }

  // Cart / wishlist state first, so /cart and /checkout render the agent's
  // snapshot the moment we navigate there. Inside the agent-action section so
  // the mutations it performs are tagged `source: "agent"`.
  beginAgentAction();
  try {
    if (typeof onLastResponse === "function" && lastResponse) {
      try {
        onLastResponse(lastResponse);
      } catch (err) {
        console.warn("[OrbieeFlow] onLastResponse threw", err);
      }
    }
    applyTurnEffects(lastResponse, agentNavigate, isSpokenTurn);
  } finally {
    endAgentAction();
  }

  return displayTextFor(lastResponse);
}
